<script setup>
import { CheckIcon } from '@heroicons/vue/24/outline'
import { SERVICES } from '@/utils/constants/services'

/**
 * LandingServices V4 - Servicios Independientes con Layout Alternante
 *
 * Actualizado para Sprint 05 - Modelo de servicios independientes
 * Consume datos desde @/utils/constants/services.js
 *
 * Tres servicios independientes (NO acumulativos):
 * - Derecho al Olvido (149 euros) - tier: olvido - Color teal
 * - Derecho al Olvido Post-Mortem (399 euros) - tier: olvido_postmortem - Color purple
 * - Testamento Digital (599 euros) - tier: testamento - Color amber
 *
 * Cada servicio tiene un target completamente diferente
 */

// Mapeo de colores por tier para las cards decorativas
const tierColorConfig = {
  olvido: {
    gradient: 'from-teal-500 to-teal-600',
    bg: 'bg-teal-500',
    bgLight: 'bg-teal-100',
    text: 'text-teal-700',
    textLight: 'text-teal-600',
    bgWhite: 'bg-white/20',
    textWhite: 'text-white/80',
    textWhiteLight: 'text-white/60',
    badgeBg: 'bg-teal-100',
    badgeText: 'text-teal-700',
    badgeTextLight: 'text-teal-600'
  },
  olvido_postmortem: {
    gradient: 'from-purple-500 to-purple-600',
    bg: 'bg-purple-900',
    bgLight: 'bg-purple-100',
    text: 'text-purple-700',
    textLight: 'text-purple-600',
    bgWhite: 'bg-white/10',
    textWhite: 'text-white/80',
    textWhiteLight: 'text-white/60',
    badgeBg: 'bg-purple-100',
    badgeText: 'text-purple-700',
    badgeTextLight: 'text-purple-600'
  },
  testamento: {
    gradient: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-500',
    bgLight: 'bg-amber-100',
    text: 'text-amber-700',
    textLight: 'text-amber-600',
    bgWhite: 'bg-white/20',
    textWhite: 'text-white/80',
    textWhiteLight: 'text-white/60',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
    badgeTextLight: 'text-amber-600'
  }
}

// Textos legales por servicio
const legalQuotes = {
  olvido: {
    title: 'Derecho de Supresión',
    text: 'El interesado tendrá derecho a obtener la supresión de los datos personales que le conciernan...',
    source: 'Artículo 17, Reglamento (UE) 2016/679',
    legalBasis: 'GDPR Art. 17'
  },
  olvido_postmortem: {
    title: 'Herencia Digital',
    text: 'Las personas vinculadas al fallecido podrán solicitar el acceso a los contenidos o su supresión...',
    source: 'Artículo 3, LOPDGDD 3/2018',
    legalBasis: 'LOPDGDD Art. 3'
  },
  testamento: {
    title: 'Planificación Digital',
    text: 'El testador podrá incluir disposiciones sobre su patrimonio digital y designar albacea digital...',
    source: 'Código Civil, Art. 675 y ss.',
    legalBasis: 'Código Civil'
  }
}

// Obtener configuracion de color por tier
const getColorConfig = (tier) => {
  return tierColorConfig[tier] || tierColorConfig.olvido
}

// Obtener quote legal por tier
const getLegalQuote = (tier) => {
  return legalQuotes[tier] || legalQuotes.olvido
}
</script>

<template>
  <section id="servicios" class="py-24 bg-bg-primary">
    <div class="max-w-7xl mx-auto px-6">
      <!-- Section Header -->
      <div class="max-w-2xl mb-20">
        <span class="text-sm font-semibold text-accent-600 uppercase tracking-widest mb-4 block">Nuestros servicios</span>
        <h2 class="text-4xl md:text-5xl font-bold text-text-primary leading-tight">
          Tres formas de proteger tu
          <span class="text-accent-600">privacidad digital</span>
        </h2>
      </div>

      <!-- Services - Alternating Layout -->
      <div class="space-y-24">

        <!-- Service Cards con layout alternante -->
        <template v-for="(service, index) in SERVICES" :key="service.id">
          <div class="grid lg:grid-cols-2 gap-12 items-center">
            <!-- Content Side -->
            <div :class="[index % 2 === 0 ? 'animate-slide-left' : 'animate-slide-right']">
              <!-- Badge -->
              <div
                class="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                :class="[getColorConfig(service.tier).badgeBg]"
              >
                <span class="text-sm font-semibold" :class="[getColorConfig(service.tier).badgeText]">
                  {{ String(index + 1).padStart(2, '0') }}
                </span>
                <span class="text-sm" :class="[getColorConfig(service.tier).badgeTextLight]">
                  {{ service.name }}
                </span>
                <span
                  v-if="service.popular"
                  class="ml-2 px-2 py-0.5 text-xs font-bold bg-teal-600 text-white rounded-full"
                >
                  Popular
                </span>
              </div>

              <!-- Title -->
              <h3 class="text-3xl font-bold text-text-primary mb-4">
                {{ service.shortDescription }}
              </h3>

              <!-- Description -->
              <p class="text-lg text-text-secondary mb-6 leading-relaxed">
                {{ service.description }}
              </p>

              <!-- Features -->
              <ul class="space-y-3 mb-8">
                <li
                  v-for="feature in service.features.slice(0, 3)"
                  :key="feature"
                  class="flex items-center gap-3 text-text-secondary"
                >
                  <div
                    class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    :class="[getColorConfig(service.tier).bgLight]"
                  >
                    <CheckIcon class="w-4 h-4" :class="[getColorConfig(service.tier).textLight]" />
                  </div>
                  {{ feature }}
                </li>
              </ul>

              <!-- Price and Legal Basis -->
              <div class="flex items-center gap-4">
                <span class="text-2xl font-bold text-text-primary">{{ service.price }}&#8364;</span>
                <span
                  class="px-3 py-1 text-sm font-medium rounded-full"
                  :class="[`${getColorConfig(service.tier).bgLight} ${getColorConfig(service.tier).text}`]"
                >
                  {{ getLegalQuote(service.tier).legalBasis }}
                </span>
              </div>
            </div>

            <!-- Decorative Card Side -->
            <div
              :class="[
                index % 2 === 0 ? 'animate-slide-right lg:order-first order-last' : 'animate-slide-left'
              ]"
            >
              <!-- Card con gradiente para servicio popular (olvido) -->
              <div
                v-if="service.tier === 'olvido'"
                class="rounded-2xl p-8 text-white bg-gradient-to-br"
                :class="[getColorConfig(service.tier).gradient]"
              >
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <component :is="service.icon" class="w-6 h-6" />
                  </div>
                  <span class="text-xl font-semibold">{{ getLegalQuote(service.tier).title }}</span>
                </div>
                <p class="text-white/80 mb-6">
                  {{ getLegalQuote(service.tier).text }}
                </p>
                <p class="text-sm text-white/60">-- {{ getLegalQuote(service.tier).source }}</p>
              </div>

              <!-- Card oscura para post-mortem -->
              <div
                v-else-if="service.tier === 'olvido_postmortem'"
                class="rounded-2xl p-8 text-white"
                :class="[getColorConfig(service.tier).bg]"
              >
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <component :is="service.icon" class="w-6 h-6" />
                  </div>
                  <span class="text-xl font-semibold">{{ getLegalQuote(service.tier).title }}</span>
                </div>
                <p class="text-white/80 mb-6">
                  {{ getLegalQuote(service.tier).text }}
                </p>
                <p class="text-sm text-white/60">-- {{ getLegalQuote(service.tier).source }}</p>
              </div>

              <!-- Card amber para testamento -->
              <div
                v-else-if="service.tier === 'testamento'"
                class="rounded-2xl p-8 bg-gradient-to-br from-slate-100 to-slate-200"
              >
                <div class="flex items-center gap-3 mb-6">
                  <div class="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <component :is="service.icon" class="w-6 h-6 text-amber-600" />
                  </div>
                  <span class="text-xl font-semibold text-text-primary">{{ getLegalQuote(service.tier).title }}</span>
                </div>
                <p class="text-text-secondary mb-6">
                  {{ getLegalQuote(service.tier).text }}
                </p>
                <p class="text-sm text-text-tertiary">-- {{ getLegalQuote(service.tier).source }}</p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>
